import { IAddedUpdatedDataCount } from './shared.dto';
import { IGenerateRoomsResultDTO } from './rooms.dao';
import { AttendeeRegistrationStatus } from './attendees.dao';
import { IUserOperatorDTO } from './operators.dao';

abstract class IJobsDAO {
    abstract getAllJobsTriggeredByCurrentUser(): Promise<IJobDescriptionDTO<JobDetailsTypes, JobResultTypes | null>[]>;
    abstract markJobAsReviewed(jobId: number): Promise<void>;
}

enum JobStatus {
    InProgress = 'IN_PROGRESS',
    Completed = 'COMPLETED',
    Failed = 'FAILED'
}

enum JobType {
    MeetingsUpload = 'MEETINGS_UPLOAD',
    MeetingsCanceling = 'CANCEL_MEETINGS',
    MeetingsRemoving = 'REMOVE_MEETINGS',
    AttendeesUpload = 'ATTENDEES_UPLOAD',
    AttendeeUpdateBulk = 'ATTENDEE_UPDATE_BULK',
    DeleteAttendee = 'DELETE_ATTENDEE',
    RoomsUpload = 'ROOMS_UPLOAD',
    AssignRooms = 'ASSIGN_ROOMS',
    ResynchronizeZoomUsers = 'RESYNCHRONIZE_ZOOM_USERS',
    OperatorsUpload = 'OPERATORS_UPLOAD',
    GenerateAccessCodes = 'GENERATE_ACCESS_CODES',
    GenerateOrganizationsLinks = 'GENERATE_ORGANIZATIONS_LINKS',
    OrganizationsUpload = 'ORGANIZATIONS_UPLOAD',
    AccessCodesUpload = 'ACCESS_CODES_UPLOAD',
    GenerateRoomConnections = 'GENERATE_ROOM_CONNECTIONS',
    UploadPassportTheme = 'UPLOAD_PASSPORT_THEME',
    GenerateRooms = 'GENERATE_ROOMS',
    DeleteOrganization = 'DELETE_ORGANIZATION',
    DeleteEvent = 'DELETE_EVENT',
    UploadEmbeddedHTMLZip = 'UPLOAD_CUSTOM_PAGE_ZIP',
    MeetingOperatorsUpdateBulk = 'MEETING_OPERATORS_UPDATE_BULK',
    UpdateZoomUserHostKey = 'UPDATE_ZOOM_USER_HOST_KEY',
    PassportSettingsUpload = 'PASSPORT_SETTINGS_UPLOAD',
    RefreshZoomAccountHostKeys = 'REFRESH_ZOOM_ACCOUNT_HOST_KEYS',
    EventBlueprintTrigger = 'EVENT_BLUEPRINT_TRIGGER',
    MeetingBlueprintTrigger = 'MEETING_BLUEPRINT_TRIGGER',
    GenerateConnectionsBlueprintTrigger = 'CONNECTION_BLUEPRINT_TRIGGER',
    DeleteUniqueJoiningLinks = 'DELETE_UNIQUE_JOINING_LINKS',
}

interface IJobDescriptionDTO<T extends JobDetailsTypes, K extends JobResultTypes | null = null> {
    jobId: number;
    jobStatus: JobStatus;
    jobType: JobType;
    jobDetails: T;
    result?: K;
}

interface IOrganizationsUploadJobDetails {
    accountId: number;
    eventId: number;
    eventName: string;
}

interface IOrganizationsUploadJobResult extends IAddedUpdatedDataCount {
    failureReason: string;
}

interface IAttendeeUploadJobDetails {
    accountId: number;
    eventId: number;
    eventName: string;
}

interface IAttendeeUpdateBulkJobDetails {
    attendeeIds: number[];
    newStatus: AttendeeRegistrationStatus;
}

interface IAccessCodesUploadJobDetails {
    accountId: number;
    eventId: number;
    eventName: string;
}

interface IUploadThemeArchiveJobDetails {
    themeId: number;
    status: string;
}

interface IUploadThemeArchiveJobResult {
    failureReason: string;
}

interface IUploadCustomPageZipJobResult {
    failureReason: string;
}

interface IAttendeeUploadJobResult extends IAddedUpdatedDataCount {
    failureReason: string;
}

interface IAttendeeUpdateBulkJobError {
    failureReason: string;
}
interface IAttendeeUpdateBulkJobResultSuccess {
    sendRegistrationEmail: boolean;
}

type IAttendeeUpdateBulkJobResult = IAttendeeUpdateBulkJobResultSuccess & IAttendeeUpdateBulkJobError;

interface IAccessCodesUploadJobResult {
    failureReason: string;
}
interface IPassportConfigurationUpload {
    menuBarLogoImageSuccessfulUpload: boolean | null;
    logoImageSuccessfulUpload: boolean | null;
    backgroundImageSuccessfulUpload: boolean | null;
    footerLogoImageSuccessfulUpload: boolean | null;
}

interface IEmailConfigurationForm {
    logoImageSuccessfulUpload: boolean | null;
}

interface IPassportConfigurationUploadJobResult {
    failureReason?: string;
    errorMessage?: string;
    passportSettings?: IPassportConfigurationUpload;
    customPageFailureReason?: string;
    emailConfigurationForm?: IEmailConfigurationForm;
}

interface IGenerateAccessCodesJobDetails {
    accountId: number;
    eventId: number;
    eventName: string;
}

interface IGenerateAccessCodesJobResult {
    generatedAccessCodes: number;
    couldNotBeGeneratedCodes: number;
}

interface IGenerateOrganizationsLinksJobResult {
    countOfOrganizations: number;
}

interface IRoomsOperatorsUploadJobDetails {
    accountId: number;
    eventId: number;
}

interface IRoomsOperatorsJobResult {
    errorMessage: string;
    duplicatedEmails: string[];
    notFoundEmails: string[];
    addedCount: number;
    roomsCount: number;
}

interface IMeetingsJobDetails {
    eventName: string;
    accountId: number;
    eventId: number;
    meetingCount: number;
}

interface IMeetingsJobResult {
    failureReason: string;
}

interface IMeetingsCancellingJobResult {
    cancelledMeetings: number;
}
interface IMeetingsRemovingJobResult {
    removedMeetings: number;
}

interface IRoomsUploadJobDetails {
    eventName: string;
    meetingCount: number;
    accountId: number;
    eventId: number;
}

interface IAssignRoomsJobDetails {
    accountId: number;
    eventId: number;
    eventName: string;
}

interface IAssignRoomsJobResult {
    assignedRooms: number;
    couldNotBeAssignedRooms: number;
}

interface IUploadMeetingsJobResultError {
    failureReason: string;
}

interface IUploadMeetingsJobResultSuccess {
    createdAttendeeCount: number;
    createdMeetingCount: number;
    createdOrganizationCount: number;
    updatedAttendeeCount: number;
    updatedMeetingCount: number;
    updatedOrganizationCount: number;
}

type IUploadMeetingsJobResult = IUploadMeetingsJobResultSuccess & IUploadMeetingsJobResultError;


interface IDeleteAttendeeJobResult {
    firstName: string;
    lastName: string;
}

interface IDeleteOrganizationJobResult {
    name: string;
}

interface ICreateZoomMeetingsJobResult {
    createdZoomMeetingCount: number;
    failedCreateZoomMeetingCount: number;
}

interface ICreateZoomConnectionsJobResult {
    createdZoomMeetingCount: number;
    failedCreateZoomMeetingCount: number;
}

interface IMeetingOperatorsUpdateBulkJobDetails {
    meetingIds: number[];
    bulkAddedOperators: IUserOperatorDTO[];
}

interface IMeetingOperatorsUpdateBulkJobResult {
    failureReason: string;
}

interface IEventBlueprintTriggerJobDetails {
    accountId: number;
    eventId: number;
    eventName: string;
}
interface IEventBlueprintTriggerJobResults {}

interface IMeetingBlueprintTriggerJobDetails {
    accountId: number;
    eventId: number;
    eventName: string;
}
interface IMeetingBlueprintTriggerJobResults {}

interface IGenerateConnectionsJobDetails {
    accountId?: number;
    eventId?: number;
    eventName?: string;
    meetingCount?: number;
}
interface IGenerateConnectionsJobResult {
    eventId?: number;
}

interface IDeleteUniqueJoiningLinksJobDetails {
    accountId: number;
    eventId: number;
    meetingId: number;
}
interface IDeleteUniqueJoiningLinksJobResult {}

type JobDetailsTypes =
    | IMeetingsJobDetails
    | IRoomsUploadJobDetails
    | IAssignRoomsJobDetails
    | IRoomsOperatorsUploadJobDetails
    | IAttendeeUploadJobDetails
    | IAttendeeUpdateBulkJobDetails
    | IGenerateAccessCodesJobDetails
    | IUploadThemeArchiveJobDetails
    | IMeetingOperatorsUpdateBulkJobDetails
    | IEventBlueprintTriggerJobDetails
    | IMeetingBlueprintTriggerJobDetails
    | IGenerateConnectionsJobDetails
    | IDeleteUniqueJoiningLinksJobDetails
;

type JobResultTypes =
    | IAssignRoomsJobResult
    | ICreateZoomMeetingsJobResult
    | ICreateZoomConnectionsJobResult
    | IMeetingsJobResult
    | IMeetingsCancellingJobResult
    | IMeetingsRemovingJobResult
    | IRoomsOperatorsJobResult
    | IAttendeeUploadJobResult
    | IAttendeeUpdateBulkJobResult
    | IGenerateAccessCodesJobResult
    | IUploadThemeArchiveJobResult
    | IGenerateRoomsResultDTO
    | IDeleteAttendeeJobResult
    | IDeleteOrganizationJobResult
    | IUploadMeetingsJobResult
    | IGenerateOrganizationsLinksJobResult
    | IMeetingOperatorsUpdateBulkJobResult
    | IEventBlueprintTriggerJobResults
    | IMeetingBlueprintTriggerJobResults
    | IGenerateConnectionsJobResult
    | IDeleteUniqueJoiningLinksJobResult
;

export {
    JobStatus,
    JobType,
    JobResultTypes,
    JobDetailsTypes,
    IJobsDAO,
    IJobDescriptionDTO,
    IMeetingsJobDetails,
    IMeetingsJobResult,
    IAssignRoomsJobDetails,
    IAssignRoomsJobResult,
    IMeetingsCancellingJobResult,
    IRoomsUploadJobDetails,
    ICreateZoomMeetingsJobResult,
    ICreateZoomConnectionsJobResult,
    IDeleteAttendeeJobResult,
    IDeleteOrganizationJobResult,
    IRoomsOperatorsUploadJobDetails,
    IRoomsOperatorsJobResult,
    IAttendeeUploadJobDetails,
    IAttendeeUploadJobResult,
    IAttendeeUpdateBulkJobResult,
    IGenerateAccessCodesJobDetails,
    IGenerateAccessCodesJobResult,
    IOrganizationsUploadJobDetails,
    IOrganizationsUploadJobResult,
    IAccessCodesUploadJobDetails,
    IAttendeeUpdateBulkJobDetails,
    IAccessCodesUploadJobResult,
    IUploadMeetingsJobResult,
    IUploadThemeArchiveJobDetails,
    IUploadThemeArchiveJobResult,
    IMeetingsRemovingJobResult,
    IGenerateOrganizationsLinksJobResult,
    IUploadCustomPageZipJobResult,
    IMeetingOperatorsUpdateBulkJobDetails,
    IMeetingOperatorsUpdateBulkJobResult,
    IPassportConfigurationUploadJobResult,
    IEventBlueprintTriggerJobDetails,
    IEventBlueprintTriggerJobResults,
    IMeetingBlueprintTriggerJobDetails,
    IMeetingBlueprintTriggerJobResults,
    IGenerateConnectionsJobDetails,
    IGenerateConnectionsJobResult,
    IDeleteUniqueJoiningLinksJobDetails,
    IDeleteUniqueJoiningLinksJobResult,
};
